import { Button, FormControl, FormLabel, Input, Select, VStack } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { LocalDate, LocalTime, ZoneId } from "@js-joda/core";
import "@js-joda/timezone";
import React from "react";

import useApi from "../../../shared/hooks/useApi";
import { SavedReportId } from "../../../shared/schema/schema";
import RecipientEmailsInput from "./RecipientEmailsInput";
import { BodyOf } from "../../../core/api";

type ScheduledSavedReportParams = BodyOf<"post", "./saved_report/:savedReportId/scheduled">;
type PeriodType = "DAILY" | "WEEKLY" | "MONTHLY";

const WEEKDAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

type Props = {
  reportType: "saved" | "standard";
  reportId: number;
  onCreated: () => void;
};

export default function ScheduleReportForm({ onCreated, reportId, reportType }: Props) {
  const { mutations } = useApi();

  const [periodType, setPeriodType] = React.useState<PeriodType>("WEEKLY");
  const [dayOfWeek, setDayOfWeek] = React.useState("MONDAY");
  const [dayOfMonth, setDayOfMonth] = React.useState(1);
  const [time, setTime] = React.useState("09:00");
  const [recipients, setRecipients] = React.useState<string[]>([]);

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const createMutationOptions =
    reportType === "saved"
      ? mutations.scheduledReports.createSchedule(reportId as SavedReportId)
      : mutations.scheduledReports.createStandardSchedule(reportId);

  const create = useMutation({
    ...createMutationOptions,
    onSuccess: () => {
      setRecipients([]);
      onCreated();
    },
  });

  const submit = () => {
    const zone = ZoneId.of(timezone);
    const [hour, minute] = time.split(":").map(Number);
    // time_in_day is sent as today's date at the chosen local time; the server
    // derives next_runtime and rolls dates relative to each run.
    const timeInDayDate = LocalDate.now(zone)
      .atTime(LocalTime.of(hour, minute))
      .atZone(zone)
      .toInstant();

    const body: ScheduledSavedReportParams = {
      periodType,
      timeInDayDate,
      interval: periodType === "DAILY" ? null : 1,
      dayOfWeek: periodType === "WEEKLY" ? dayOfWeek : null,
      dayOfMonth: periodType === "MONTHLY" ? dayOfMonth : null,
      timezone,
      recipientEmails: recipients,
      reportTemplate: null,
    };

    create.mutate(body);
  };

  return (
    <VStack align="stretch" spacing={4}>
      <FormControl>
        <FormLabel>Frequency</FormLabel>
        <Select value={periodType} onChange={(e) => setPeriodType(e.target.value as PeriodType)}>
          <option value="DAILY">Daily</option>
          <option value="WEEKLY">Weekly</option>
          <option value="MONTHLY">Monthly</option>
        </Select>
      </FormControl>

      {periodType === "WEEKLY" && (
        <FormControl>
          <FormLabel>Day of week</FormLabel>
          <Select value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)}>
            {WEEKDAYS.map((d) => (
              <option key={d} value={d}>
                {d.charAt(0) + d.slice(1).toLowerCase()}
              </option>
            ))}
          </Select>
        </FormControl>
      )}

      {periodType === "MONTHLY" && (
        <FormControl>
          <FormLabel>Day of month</FormLabel>
          <Input
            max={31}
            min={1}
            type="number"
            value={dayOfMonth}
            onChange={(e) => setDayOfMonth(Number(e.target.value))}
          />
        </FormControl>
      )}

      <FormControl>
        <FormLabel>Time ({timezone})</FormLabel>
        <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
      </FormControl>

      <RecipientEmailsInput value={recipients} onChange={setRecipients} />

      <Button
        colorScheme="blue"
        isDisabled={recipients.length === 0}
        isLoading={create.isPending}
        onClick={submit}
      >
        Schedule report
      </Button>
    </VStack>
  );
}
